import { useEffect, useState } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { FileText, Download, AlertTriangle, Info, Plus, Trash2, UploadCloud } from 'lucide-react';

const emptyForm = () => ({ documentName: '', description: '', expiryDate: '', reminderDays: 30, file: null });

export default function MyDocumentsPage() {
  const toast = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    return employeeApi.getMyDocuments()
      .then((res) => setDocuments(res.documents || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    if (!form.file) { toast.error('Please choose a file'); return; }
    if (!form.documentName.trim()) { toast.error('Please give the document a name'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('documentName', form.documentName.trim());
      fd.append('description', form.description.trim());
      fd.append('expiryDate', form.expiryDate || '');
      fd.append('reminderDays', String(form.reminderDays || 30));
      await employeeApi.uploadMyDocument(fd);
      toast.success('Document uploaded');
      setShowAdd(false);
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.documentName || doc.fileName}"? This can't be undone.`)) return;
    setDeletingId(doc.rowKey);
    try {
      await employeeApi.deleteMyDocument(doc.rowKey);
      toast.success('Document deleted');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally { setDeletingId(null); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Documents</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your HR documents. Upload your own (passport, ID, certificates…) and set an expiry
            reminder — we'll email you before it lapses.
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setShowAdd(true); }}>
          <Plus className="h-4 w-4" /> Add document
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-8">
            <Info className="mt-0.5 h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">No documents yet</p>
              <p className="text-xs text-muted-foreground mt-1">Use “Add document” to upload one and set an expiry reminder, or your admin may add some for you.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => {
            const daysLeft = doc.expiryDate ? Math.ceil((new Date(doc.expiryDate) - new Date()) / 86400000) : null;
            const isExpired = daysLeft !== null && daysLeft <= 0;
            const isExpiring = daysLeft !== null && daysLeft > 0 && daysLeft <= (doc.reminderDays || 30);

            return (
              <Card key={`${doc.scope || ''}-${doc.rowKey}`}>
                {(isExpired || isExpiring) && (
                  <div className={`flex items-center gap-2 px-6 py-2 text-xs font-medium ${isExpired ? 'bg-destructive/5 text-destructive' : 'bg-warning/5 text-warning'}`}>
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {isExpired ? `Expired on ${doc.expiryDate}` : `Expiring in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                  </div>
                )}
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground flex items-center gap-2">
                      {doc.documentName || doc.typeName || doc.rowKey}
                      {doc.selfUploaded && <Badge variant="default">Yours</Badge>}
                      {doc.scope === 'organization' && <Badge variant="default">Org</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {doc.typeName || ''}
                      {doc.fileName && ` · ${doc.fileName}`}
                      {doc.expiryDate && ` · Expires ${doc.expiryDate}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isExpired && <Badge variant="danger">Expired</Badge>}
                    {isExpiring && <Badge variant="warning">{daysLeft}d left</Badge>}
                    {!isExpired && !isExpiring && doc.expiryDate && <Badge variant="success">Active</Badge>}
                    {doc.fileMissing ? (
                      <span
                        title="The file for this document is missing from storage."
                        className="flex items-center gap-1.5 rounded-lg bg-destructive/10 px-3 py-1.5 text-xs font-semibold text-destructive"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" /> File unavailable
                      </span>
                    ) : doc.downloadUrl ? (
                      <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:shadow-md transition-all">
                        <Download className="h-3.5 w-3.5" /> Download
                      </a>
                    ) : null}
                    {doc.selfUploaded && (
                      <button
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.rowKey}
                        title="Delete"
                        className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showAdd && (
        <Modal isOpen onClose={() => setShowAdd(false)} title="Add a document" width="min(560px, 96vw)">
          <div className="space-y-4">
            <Input label="Document name" value={form.documentName} onChange={(e) => setForm({ ...form, documentName: e.target.value })} placeholder="e.g. Passport" />
            <Input label="Notes (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Expiry date (optional)" type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
              <Input label="Remind me (days before)" type="number" min="1" value={form.reminderDays} onChange={(e) => setForm({ ...form, reminderDays: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-1">File</label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary/50">
                <UploadCloud className="h-4 w-4" />
                {form.file ? form.file.name : 'Choose a file (PDF, image, …)'}
                <input type="file" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              If you set an expiry date, we'll email you {form.reminderDays || 30} days before it expires.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleUpload} loading={saving} disabled={saving}>Upload</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
